import type { FieldTemplate } from "@/lib/wartungen";
import {
  BEHAVIOR_OPERATOR_LABELS,
  coerceRuleValueForOperator,
  isBooleanSourceField,
  normalizeOperator,
  type SourceFieldMeta,
} from "./behaviorRules";

export interface PlaceholderBinding {
  placeholder: string;
  source_field_key: string;
}

/** Verknüpfung mit der vorherigen Bedingung. „oder“ startet eine Alternative, „und“ gilt innerhalb davon. */
export type PlanRuleJoin = "and" | "or";

export interface SymbolRule {
  rule_type: string;
  source_scope?: string;
  source_field_key?: string;
  operator?: string;
  value?: unknown;
  /** Nur ab der zweiten Bedingung. Fehlt der Wert, gilt „und“. */
  join?: PlanRuleJoin;
}

export interface PlanSourceField extends SourceFieldMeta {
  trueLabel: string;
  falseLabel: string;
}

export interface PlanSymbolFieldRef {
  field_key: string;
  label: string;
  type: string;
  scope: string;
  template_id?: number | null;
  options_override_json?: Record<string, unknown>;
  ui_override_json?: Record<string, unknown>;
}

export const PLAN_LABEL_BUILTIN_SOURCES = [
  { key: "__plan_asset_name__", label: "Objektname" },
  { key: "__plan_asset_external_id__", label: "Externe Objekt-ID" },
] as const;

export function stripDataUrlPrefix(value: string): string {
  return String(value || "").replace(/^data:[^,]*,/, "");
}

/** Vollständige `{{ … }}`-Tokens wie in der klassischen Feld-Konfiguration. */
export function extractPlaceholderTokens(svgMarkup: string): string[] {
  const text = String(svgMarkup || "");
  const re = /\{\{\s*[^{}]+\s*\}\}/g;
  const seen = new Set<string>();
  const out: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    const token = match[0];
    if (!seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

export function svgMarkupToDataUrl(svgMarkup: string): string {
  const raw = String(svgMarkup || "").trim();
  if (!raw) return "";
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(raw)}`;
}

export function pngBase64ToSrc(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  return trimmed.startsWith("data:") ? trimmed : `data:image/png;base64,${trimmed}`;
}

export function normalizePlaceholderBindings(raw: unknown): PlaceholderBinding[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = entry as Record<string, unknown>;
      return {
        placeholder: String(row.placeholder ?? row.token ?? "").trim(),
        source_field_key: String(row.source_field_key ?? "").trim(),
      };
    })
    .filter((b) => b.placeholder || b.source_field_key);
}

const OPTION_LIST_KEYS = ["options", "choices", "items", "rows"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function optionList(config: Record<string, unknown>): Record<string, unknown>[] {
  for (const key of OPTION_LIST_KEYS) {
    const raw = config[key];
    if (!Array.isArray(raw) || raw.length === 0) continue;
    return raw.filter(isRecord);
  }
  return [];
}

function storedOptionValue(row: Record<string, unknown>): string {
  for (const key of ["value", "key", "id"]) {
    const raw = row[key];
    if (raw == null || typeof raw === "object") continue;
    const text = String(raw).trim();
    if (text) return text;
  }
  return "";
}

function storedOptionLabel(row: Record<string, unknown>, fallback: string): string {
  for (const key of ["label", "text", "name"]) {
    const raw = row[key];
    if (raw == null || typeof raw === "object") continue;
    const text = String(raw).trim();
    if (text) return text;
  }
  return fallback;
}

function mergedOptionConfig(field: PlanSymbolFieldRef, template: FieldTemplate | undefined): Record<string, unknown> {
  const base = asRecord(template?.default_options_json);
  const override = asRecord(field.options_override_json);
  if (optionList(override).length) return override;
  if (optionList(base).length) return base;
  return { ...base, ...override };
}

/** Stammdatenfelder samt Antwortoptionen (Feld-Override, sonst Template). */
export function planSymbolSourceFields(
  fields: PlanSymbolFieldRef[],
  templates: FieldTemplate[] = [],
): PlanSourceField[] {
  const templatesById = new Map(templates.map((template) => [template.id, template]));
  const seen = new Set<string>();
  const out: PlanSourceField[] = [];
  for (const field of fields) {
    if (field.scope !== "masterdata") continue;
    const key = String(field.field_key || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const template = field.template_id != null ? templatesById.get(field.template_id) : undefined;
    const ui = { ...asRecord(template?.default_ui_json), ...asRecord(field.ui_override_json) };
    const valueOptions = optionList(mergedOptionConfig(field, template))
      .filter((row) => row.active !== false)
      .map((row) => {
        const value = storedOptionValue(row);
        return { value, label: storedOptionLabel(row, value) };
      })
      .filter((row) => row.value);
    out.push({
      scope: "masterdata",
      key,
      label: String(field.label || key).trim() || key,
      type: field.type,
      valueOptions,
      trueLabel: String(ui.labelTrue || ui.label_true || "Ja").trim() || "Ja",
      falseLabel: String(ui.labelFalse || ui.label_false || "Nein").trim() || "Nein",
    });
  }
  return out;
}

/** „oder“ trennt Alternativen. Innerhalb einer Alternative gilt „und“. Die erste Bedingung startet immer die erste Alternative. */
export function groupPlanSymbolRules<T extends { join?: string }>(rules: T[]): T[][] {
  const groups: T[][] = [];
  let current: T[] = [];
  rules.forEach((rule, index) => {
    if (current.length > 0 && index > 0 && rule.join === "or") {
      groups.push(current);
      current = [rule];
    } else {
      current.push(rule);
    }
  });
  if (current.length) groups.push(current);
  return groups;
}

export function formatPlanVisibilitySentence(clauses: { join?: string; text: string }[]): string {
  const groups = groupPlanSymbolRules(clauses)
    .map((group) => group.map((clause) => clause.text.trim()).filter(Boolean))
    .filter((group) => group.length > 0);
  const single = groups[0];
  if (!single) return "";
  if (groups.length === 1) return single.join(" und ");
  return groups.map((group) => `(${group.join(" und ")})`).join(" oder ");
}

export function coercePlanBooleanAnswer(value: unknown): unknown {
  if (typeof value === "boolean" || Array.isArray(value)) return value;
  const token = String(value ?? "").trim().toLowerCase();
  if (token === "true" || token === "1" || token === "ja" || token === "yes") return true;
  if (token === "false" || token === "0" || token === "nein" || token === "no") return false;
  return value;
}

/** Auswahlwert für Ja/Nein. „ja“/„nein“ aus älteren Freitext-Regeln gelten als Ja/Nein. */
export function planBooleanSelection(value: unknown): string {
  if (value === true) return "true";
  if (value === false) return "false";
  const token = String(value ?? "").trim().toLowerCase();
  if (!token) return "";
  if (token === "true" || token === "1" || token === "ja" || token === "yes") return "true";
  if (token === "false" || token === "0" || token === "nein" || token === "no") return "false";
  return String(value);
}

function answerLabels(value: unknown, source: PlanSourceField | null): string[] {
  const one = (raw: unknown): string => {
    if (source && isBooleanSourceField(source)) {
      const selected = planBooleanSelection(raw);
      if (selected === "true") return source.trueLabel;
      if (selected === "false") return source.falseLabel;
    }
    const token = String(raw ?? "").trim();
    if (!token) return "";
    return source?.valueOptions.find((option) => option.value === token)?.label || token;
  };
  const entries = Array.isArray(value) ? value : [value];
  return entries.map(one).filter(Boolean);
}

export function planRuleClauseText(rule: SymbolRule, source: PlanSourceField | null): string {
  const field = source?.label?.trim() || "";
  if (!field) return "";
  const labels = answerLabels(rule.value, source);
  const shown = labels.length ? labels.map((label) => `„${label}“`).join(", ") : "…";
  const operator = BEHAVIOR_OPERATOR_LABELS[normalizeOperator(rule.operator)] || "ist gleich";
  return `${field} ${operator} ${shown}`;
}

export function normalizePlanSymbolRulesForSave(
  rules: SymbolRule[],
  sources: PlanSourceField[] = [],
): SymbolRule[] {
  return rules
    .filter((rule) => rule.rule_type === "visibleIf")
    .map((rule, index) => {
      const operator = normalizeOperator(rule.operator);
      const source = sources.find((entry) => entry.key === String(rule.source_field_key || "").trim()) ?? null;
      let value = rule.value;
      if (source && isBooleanSourceField(source)) value = coercePlanBooleanAnswer(value);
      const next: SymbolRule = {
        rule_type: "visibleIf",
        source_scope: "masterdata",
        source_field_key: String(rule.source_field_key || "").trim(),
        operator,
        value: coerceRuleValueForOperator(operator, value),
      };
      if (index > 0 && rule.join === "or") next.join = "or";
      return next;
    });
}

export async function rasterizeSvgToPng(svg: string): Promise<string> {
  return new Promise((resolve) => {
    try {
      const dataUrl = svgMarkupToDataUrl(svg);
      if (!dataUrl) {
        resolve("");
        return;
      }
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.drawImage(img, 0, 0, 128, 128);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve("");
      img.src = dataUrl;
    } catch {
      resolve("");
    }
  });
}
